import { useEffect, useRef } from "react";

const warnWakeLockFailure = (action: "request" | "release", error: unknown) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[useScreenWakeLock] Failed to ${action} screen wake lock:`, error);
  }
};

/**
 * Best-effort screen wake lock for a visible, active browser session.
 * Browsers release screen locks when a document becomes hidden, so the hook
 * reacquires on foreground while `enabled` remains true.
 */
export function useScreenWakeLock(enabled: boolean): void {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const requestGenerationRef = useRef(0);

  useEffect(() => {
    if (
      !enabled ||
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      typeof navigator === "undefined" ||
      !window.isSecureContext ||
      !("wakeLock" in navigator) ||
      !navigator.wakeLock
    ) {
      return;
    }

    let disposed = false;

    const releaseSentinel = async (sentinel: WakeLockSentinel) => {
      if (sentinelRef.current === sentinel) {
        sentinelRef.current = null;
      }
      if (sentinel.released) return;

      try {
        await sentinel.release();
      } catch (error) {
        warnWakeLockFailure("release", error);
      }
    };

    const requestLock = async () => {
      if (
        disposed ||
        document.visibilityState !== "visible" ||
        (sentinelRef.current && !sentinelRef.current.released)
      ) {
        return;
      }

      requestGenerationRef.current += 1;
      const requestGeneration = requestGenerationRef.current;

      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (
          disposed ||
          document.visibilityState !== "visible" ||
          requestGenerationRef.current !== requestGeneration
        ) {
          await releaseSentinel(sentinel);
          return;
        }

        sentinelRef.current = sentinel;
        const handleRelease = () => {
          sentinel.removeEventListener("release", handleRelease);
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
          }
        };
        sentinel.addEventListener("release", handleRelease);
      } catch (error) {
        if (!disposed && requestGenerationRef.current === requestGeneration) {
          warnWakeLockFailure("request", error);
        }
      }
    };

    const handleVisibilityChange = () => {
      requestGenerationRef.current += 1;
      if (document.visibilityState === "visible") {
        void requestLock();
        return;
      }

      const sentinel = sentinelRef.current;
      if (sentinel) {
        void releaseSentinel(sentinel);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    void requestLock();

    return () => {
      disposed = true;
      requestGenerationRef.current += 1;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      const sentinel = sentinelRef.current;
      if (sentinel) {
        void releaseSentinel(sentinel);
      }
    };
  }, [enabled]);
}
